import React from 'react';
import { useBreakpoint } from 'hooks';
import { Tabs, TabPane, Badge, Space } from 'components';
import { AuthContext, LayoutContext } from 'contexts';
import { StyledPaneTitle, StyledUsersCount } from './StyledComponents';
import ManageUsersContext, { TabKeyType } from './ManageUsersContext';

import {
  UsersTable,
  TransfersTable,
  GroupsList,
  TeamsList,
  DeletedTable,
} from './Tables';

type TabPaneType = {
  label: string | JSX.Element;
  key: TabKeyType;
  content: JSX.Element;
};

const ManageUsersTabs: React.FC = () => {
  const { setAppTitle } = React.useContext(LayoutContext);
  const { hasFeature } = React.useContext(AuthContext);
  const {
    activeUsersCount,
    groupsCount,
    teamsCount,
    deletedUsersCount,
    ongoingTransfersCount,
    selectedTabKey,
    setBreadcrumbRoutes,
    setSelectedTabKey,
  } = React.useContext(ManageUsersContext);
  const { md } = useBreakpoint();

  React.useEffect(() => {
    setAppTitle('Manage Users');
    setBreadcrumbRoutes([]);
  }, [setAppTitle, setBreadcrumbRoutes]);

  const tabPaneList: TabPaneType[] = [
    { label: 'Users', key: 'users', content: <UsersTable /> },
    {
      label: (
        <Space>
          Transfers
          <Badge
            count={ongoingTransfersCount}
            offset={[0, -1]}
            data-cy="transfers-count"
          />
        </Space>
      ),
      key: 'transfers',
      content: <TransfersTable />,
    },
    { label: 'Groups', key: 'groups', content: <GroupsList /> },
    ...(hasFeature('TEAMS')
      ? [
          {
            label: 'Teams',
            key: 'teams' as TabKeyType,
            content: <TeamsList />,
          },
        ]
      : []),
    { label: 'Deleted', key: 'deleted', content: <DeletedTable /> },
  ];

  let countContent = '';
  switch (selectedTabKey) {
    case 'users': {
      countContent = `${activeUsersCount} User${
        activeUsersCount !== 1 ? 's' : ''
      }`;
      break;
    }
    case 'groups': {
      countContent = `${groupsCount} Group${groupsCount !== 1 ? 's' : ''}`;
      break;
    }
    case 'deleted': {
      countContent = `${deletedUsersCount} User${
        deletedUsersCount !== 1 ? 's' : ''
      }`;
      break;
    }
    case 'teams': {
      countContent = `${teamsCount} Team${teamsCount !== 1 ? 's' : ''}`;
      break;
    }
    default:
      break;
  }

  return (
    <Tabs
      activeKey={selectedTabKey}
      onTabClick={key => setSelectedTabKey(key as TabKeyType)}
      destroyInactiveTabPane
      tabBarStyle={{ margin: 0, padding: `0 ${md ? 20 : 10}px` }}
      tabBarGutter={md ? 32 : 20}
      tabBarExtraContent={
        <StyledUsersCount data-cy="user-count">{countContent}</StyledUsersCount>
      }
    >
      {tabPaneList.map(({ label, key, content }) => (
        <TabPane
          key={key}
          tab={
            <StyledPaneTitle
              $active={key === selectedTabKey}
              data-cy={`users-tab-${key}`}
            >
              {label}
            </StyledPaneTitle>
          }
        >
          {content}
        </TabPane>
      ))}
    </Tabs>
  );
};

export default ManageUsersTabs;
